import React, { useState, useEffect } from 'react';
import { X, Search, User, Phone, Users, Sun, Moon, Home, MapPin, AlertTriangle, History } from 'lucide-react';

const BusEnrollModal = ({ apiCall, selectedYear, currentTrimester, busLines, buses, onClose, onSuccess, showError }) => {
  const [modalData, setModalData] = useState({
    // Bus configuration
    busService: 'Both', // 'Both', 'MorningOnly', 'EveningOnly'
    hasDifferentBuses: false,
    morningBusNumber: '',
    eveningBusNumber: '',
    
    // Address configuration
    useCustomAddress: false,
    hasDifferentAddresses: false,
    morningAddress: '',
    eveningAddress: '',
    addressNotes: ''
  });
  
  const [studentSearch, setStudentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [familyAddress, setFamilyAddress] = useState('');
  // Last year's bus line/bus/address for reference + prior-year balance gate.
  const [priorYearInfo, setPriorYearInfo] = useState(null);
  const [overrideBusBalance, setOverrideBusBalance] = useState(false);

  // API-based student search with debounce
  useEffect(() => {
    if (studentSearch.length >= 2) {
      const timer = setTimeout(async () => {
        try {
          setSearching(true);
          const results = await apiCall(`/bus/search-students?query=${encodeURIComponent(studentSearch)}`);
          setSearchResults(results);
        } catch (error) {
          console.error('Search error:', error);
          showError('Failed to search students: ' + error.message);
        } finally {
          setSearching(false);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [studentSearch, apiCall, showError]);

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setPriorYearInfo(null);
    setOverrideBusBalance(false);

    // Get family address
    try {
      const familyData = await apiCall(`/families/${student.FamilyID}`);
      setFamilyAddress(familyData.Address || '');
    } catch (error) {
      console.error('Error loading family address:', error);
    }

    // Pull last year's bus line/bus/address (for reference) + the family's
    // prior-year bus balance (which blocks the new enrollment).
    try {
      const prior = await apiCall(`/bus/enrollments/prior-year/${student.StudentID}?academicYear=${encodeURIComponent(selectedYear)}&familyId=${encodeURIComponent(student.FamilyID)}`);
      setPriorYearInfo(prior);
    } catch (error) {
      console.error('Prior-year bus lookup failed:', error);
    }

    setModalData({
      ...modalData,
      studentId: student.StudentID,
      familyId: student.FamilyID,
      studentName: student.FullName,
      fatherName: student.FatherName,
      studentClass: student.Class,
      section: student.Section
    });
    setStudentSearch(`${student.FullName} - ${student.FatherName} (${student.Class || 'N/A'})`);
    setSearchResults([]);
  };

  // Handle bus service type change
  const handleBusServiceChange = (service) => {
    const newData = { ...modalData, busService: service };
    
    // Auto-adjust bus configuration based on service
    if (service === 'MorningOnly') {
      newData.hasDifferentBuses = false;
      newData.eveningBusNumber = '';
    } else if (service === 'EveningOnly') {
      newData.hasDifferentBuses = false;
      newData.morningBusNumber = '';
    }
    
    // Auto-adjust address configuration
    if (service === 'MorningOnly' && modalData.hasDifferentAddresses) {
      newData.hasDifferentAddresses = false;
      newData.eveningAddress = '';
    } else if (service === 'EveningOnly' && modalData.hasDifferentAddresses) {
      newData.hasDifferentAddresses = false;
      newData.morningAddress = '';
    }
    
    setModalData(newData);
  };

  // When different buses checkbox changes
  const handleDifferentBusesChange = (checked) => {
    if (checked) {
      setModalData({
        ...modalData,
        hasDifferentBuses: true,
        morningBusNumber: modalData.morningBusNumber || '',
        eveningBusNumber: modalData.eveningBusNumber || ''
      });
    } else {
      setModalData({
        ...modalData,
        hasDifferentBuses: false,
        morningBusNumber: modalData.morningBusNumber || modalData.eveningBusNumber || '',
        eveningBusNumber: modalData.morningBusNumber || modalData.eveningBusNumber || ''
      });
    }
  };

  // When same bus number changes
  const handleSameBusChange = (busNumber) => {
    setModalData({
      ...modalData,
      morningBusNumber: busNumber,
      eveningBusNumber: busNumber
    });
  };

  // Handle address configuration changes
  const handleUseCustomAddressChange = (checked) => {
    if (!checked) {
      // Switching back to family address
      setModalData({
        ...modalData,
        useCustomAddress: false,
        hasDifferentAddresses: false,
        morningAddress: '',
        eveningAddress: '',
        addressNotes: ''
      });
    } else {
      // Switching to custom address - pre-fill with family address
      setModalData({
        ...modalData,
        useCustomAddress: true,
        morningAddress: familyAddress,
        eveningAddress: familyAddress
      });
    }
  };

  const handleDifferentAddressesChange = (checked) => {
    if (checked) {
      setModalData({
        ...modalData,
        hasDifferentAddresses: true,
        morningAddress: modalData.morningAddress || familyAddress,
        eveningAddress: modalData.eveningAddress || familyAddress
      });
    } else {
      setModalData({
        ...modalData,
        hasDifferentAddresses: false,
        morningAddress: modalData.morningAddress || modalData.eveningAddress || familyAddress,
        eveningAddress: modalData.morningAddress || modalData.eveningAddress || familyAddress
      });
    }
  };

  const handleSameAddressChange = (address) => {
    setModalData({
      ...modalData,
      morningAddress: address,
      eveningAddress: address
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!modalData.studentId) {
      showError('Please select a student');
      return;
    }
    if (!modalData.lineId) {
      showError('Please select a bus line');
      return;
    }
    
    // Validate bus numbers based on service type
    if (modalData.busService === 'Both' || modalData.busService === 'MorningOnly') {
      if (!modalData.morningBusNumber) {
        showError('Please select morning bus number');
        return;
      }
    }
    if (modalData.busService === 'Both' || modalData.busService === 'EveningOnly') {
      if (!modalData.eveningBusNumber) {
        showError('Please select evening bus number');
        return;
      }
    }
    
    // Validate addresses if using custom
    if (modalData.useCustomAddress) {
      if (modalData.busService === 'Both' || modalData.busService === 'MorningOnly') {
        if (!modalData.morningAddress?.trim()) {
          showError('Please enter morning pickup address');
          return;
        }
      }
      if (modalData.busService === 'Both' || modalData.busService === 'EveningOnly') {
        if (!modalData.eveningAddress?.trim()) {
          showError('Please enter evening dropoff address');
          return;
        }
      }
    }
    
    // Check if already enrolled
    if (selectedStudent && selectedStudent.EnrolledTrimesters) {
      if (selectedStudent.EnrolledTrimesters.includes(`T${currentTrimester}`)) {
        showError(`Student is already enrolled in Trimester ${currentTrimester}`);
        return;
      }
    }

    // Prior-year bus debt gate (backend enforces too). Non-admins can't proceed;
    // admins must tick the override.
    if (priorYearInfo?.blockEnrollment && !overrideBusBalance) {
      showError(
        `This family has an outstanding ${priorYearInfo.priorYear} bus balance of $${Number(priorYearInfo.familyPriorBusBalance).toFixed(2)}. ` +
        (priorYearInfo.canOverride ? 'Tick "Override" to enroll anyway.' : 'Clear it before enrolling.')
      );
      return;
    }

    try {
      setLoading(true);
      await apiCall('/bus/enrollments', {
        method: 'POST',
        body: JSON.stringify({
          studentId: modalData.studentId,
          familyId: modalData.familyId,
          lineId: parseInt(modalData.lineId),
          overrideBusBalance: overrideBusBalance,
          
          // Bus configuration
          busService: modalData.busService,
          busNumber: parseInt(modalData.morningBusNumber || modalData.eveningBusNumber), // Legacy
          hasDifferentBuses: modalData.hasDifferentBuses,
          morningBusNumber: modalData.morningBusNumber ? parseInt(modalData.morningBusNumber) : null,
          eveningBusNumber: modalData.eveningBusNumber ? parseInt(modalData.eveningBusNumber) : null,
          
          // Address configuration
          useCustomAddress: modalData.useCustomAddress,
          hasDifferentAddresses: modalData.hasDifferentAddresses,
          morningAddress: modalData.useCustomAddress ? modalData.morningAddress : null,
          eveningAddress: modalData.useCustomAddress ? modalData.eveningAddress : null,
          addressNotes: modalData.addressNotes || null,
          
          academicYear: selectedYear,
          trimester: currentTrimester
        })
      });
      onSuccess();
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedLine = busLines.find(l => l.LineID === parseInt(modalData.lineId));

  // Last year's enrollment (for inline "last year: …" hints beside each field).
  const lastYear = priorYearInfo?.hadBusLastYear ? priorYearInfo.lastEnrollment : null;
  const lastBusHint = lastYear
    ? (lastYear.BusService === 'MorningOnly' ? `Morning #${lastYear.MorningBusNumber}`
      : lastYear.BusService === 'EveningOnly' ? `Evening #${lastYear.EveningBusNumber}`
      : lastYear.MorningBusNumber === lastYear.EveningBusNumber ? `#${lastYear.MorningBusNumber}`
      : `Morning #${lastYear.MorningBusNumber}, Evening #${lastYear.EveningBusNumber}`)
    : null;
  const lastAddrHint = lastYear
    ? (lastYear.UseCustomAddress
        ? (lastYear.HasDifferentAddresses
            ? `Custom — AM: ${lastYear.MorningAddress || '—'} / PM: ${lastYear.EveningAddress || '—'}`
            : `Custom: ${lastYear.MorningAddress || lastYear.EveningAddress || '—'}`)
        : 'Family address')
    : null;
  // Inline hint chip shown next to a field's label.
  const hint = (value) => value == null ? null : (
    <span className="ml-2 text-xs font-normal text-blue-600 inline-flex items-center gap-1 align-middle">
      <History size={11} /> last year: <span className="font-semibold">{value}</span>
    </span>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10 rounded-t-xl">
          <div>
            <h3 className="text-xl font-bold text-gray-800">Enroll Student in Bus</h3>
            <p className="text-sm text-gray-600 mt-1">
              {selectedYear} - Trimester {currentTrimester}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Student Search */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Search Student *
            </label>
            <div className="relative">
              <input
                type="text"
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Type student name, ID, father name, or phone..."
                autoComplete="off"
              />
              <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
              {searching && (
                <div className="absolute right-3 top-3.5">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-2 max-h-80 overflow-y-auto border border-gray-300 rounded-lg bg-white shadow-lg">
                {searchResults.map(student => (
                  <button
                    key={student.StudentID}
                    type="button"
                    onClick={() => selectStudent(student)}
                    className="w-full text-left px-4 py-4 hover:bg-blue-50 border-b border-gray-100"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-gray-400" />
                          <p className="font-semibold text-gray-800">{student.FullName}</p>
                          <span className="text-xs text-gray-500">({student.StudentID})</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Users size={14} />
                            {student.FatherName}
                          </span>
                          {student.PrimaryPhone && (
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {student.PrimaryPhone}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {student.Class && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                            {student.Class}{student.Section || ''}
                          </span>
                        )}
                        {student.EnrolledTrimesters && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-semibold">
                            Enrolled: {student.EnrolledTrimesters}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Student Info */}
          {selectedStudent && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <User className="text-green-600" size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-800">Selected Student</p>
                  <p className="text-sm text-green-700 mt-1">
                    <strong>{modalData.studentName}</strong> ({modalData.studentId})
                  </p>
                  <p className="text-sm text-green-700">
                    Father: {modalData.fatherName} | Family: {modalData.familyId}
                    {modalData.studentClass && ` | Class: ${modalData.studentClass}${modalData.section || ''}`}
                  </p>
                  {familyAddress && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Home size={12} />
                      Family Address: {familyAddress}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Prior-year bus balance gate */}
          {selectedStudent && priorYearInfo?.blockEnrollment && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-red-600 mt-0.5" size={18} />
                <div className="flex-1 text-sm">
                  <p className="font-semibold text-red-800">
                    Outstanding {priorYearInfo.priorYear} bus balance: ${Number(priorYearInfo.familyPriorBusBalance).toFixed(2)}
                  </p>
                  <p className="text-red-700 mt-1">
                    {priorYearInfo.canOverride
                      ? 'Enrollment is blocked until this is cleared. As an administrator you may override.'
                      : 'This family must clear the balance before enrolling for the new year.'}
                  </p>
                  {priorYearInfo.canOverride && (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={overrideBusBalance}
                        onChange={(e) => setOverrideBusBalance(e.target.checked)}
                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      />
                      <span className="text-sm font-semibold text-red-800">Override outstanding balance and enroll anyway</span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Line Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Bus Line *{hint(lastYear?.LineName)}</label>
            <select
              required
              value={modalData.lineId || ''}
              onChange={(e) => setModalData({...modalData, lineId: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a line...</option>
              {busLines.map(line => (
                <option key={line.LineID} value={line.LineID}>
                  Line {line.LineNumber} - {line.LineName} (${line.TrimesterPrice}/trimester)
                </option>
              ))}
            </select>
          </div>

          {/* Service Type Selection */}
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Bus Service Type *{hint(lastYear?.BusService)}
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => handleBusServiceChange('Both')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  modalData.busService === 'Both'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Sun size={16} />
                  <Moon size={16} />
                </div>
                <p className="text-sm font-semibold">Both Ways</p>
                <p className="text-xs text-gray-600 mt-1">Morning & Evening</p>
              </button>

              <button
                type="button"
                onClick={() => handleBusServiceChange('MorningOnly')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  modalData.busService === 'MorningOnly'
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Sun size={16} />
                </div>
                <p className="text-sm font-semibold">Morning Only</p>
                <p className="text-xs text-gray-600 mt-1">To school</p>
              </button>

              <button
                type="button"
                onClick={() => handleBusServiceChange('EveningOnly')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  modalData.busService === 'EveningOnly'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Moon size={16} />
                </div>
                <p className="text-sm font-semibold">Evening Only</p>
                <p className="text-xs text-gray-600 mt-1">From school</p>
              </button>
            </div>
          </div>

          {/* Bus Selection */}
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            {lastBusHint && (
              <p className="mb-3 text-xs text-blue-600 inline-flex items-center gap-1">
                <History size={12} /> last year's bus: <span className="font-semibold">{lastBusHint}</span>
              </p>
            )}
            <div className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                id="differentBuses"
                checked={modalData.hasDifferentBuses}
                onChange={(e) => handleDifferentBusesChange(e.target.checked)}
                disabled={modalData.busService !== 'Both'}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="differentBuses" className="text-sm font-semibold text-gray-700 cursor-pointer">
                Use different buses for morning and evening
                {modalData.busService !== 'Both' && (
                  <span className="text-xs text-gray-500 ml-2">(Only available for "Both Ways")</span>
                )}
              </label>
            </div>

            {modalData.busService === 'MorningOnly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Sun size={16} className="text-orange-500" />
                    Morning Bus *
                  </div>
                </label>
                <select
                  required
                  value={modalData.morningBusNumber}
                  onChange={(e) => setModalData({...modalData, morningBusNumber: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select morning bus...</option>
                  {buses.map(bus => (
                    <option key={bus.BusID} value={bus.BusNumber}>
                      Bus #{bus.BusNumber} {bus.Notes ? `(${bus.Notes})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {modalData.busService === 'EveningOnly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Moon size={16} className="text-indigo-500" />
                    Evening Bus *
                  </div>
                </label>
                <select
                  required
                  value={modalData.eveningBusNumber}
                  onChange={(e) => setModalData({...modalData, eveningBusNumber: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select evening bus...</option>
                  {buses.map(bus => (
                    <option key={bus.BusID} value={bus.BusNumber}>
                      Bus #{bus.BusNumber} {bus.Notes ? `(${bus.Notes})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {modalData.busService === 'Both' && !modalData.hasDifferentBuses && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bus Number (Morning & Evening) *
                </label>
                <select
                  required
                  value={modalData.morningBusNumber}
                  onChange={(e) => handleSameBusChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a bus...</option>
                  {buses.map(bus => (
                    <option key={bus.BusID} value={bus.BusNumber}>
                      Bus #{bus.BusNumber} {bus.Notes ? `(${bus.Notes})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {modalData.busService === 'Both' && modalData.hasDifferentBuses && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center gap-2">
                      <Sun size={16} className="text-orange-500" />
                      Morning Bus *
                    </div>
                  </label>
                  <select
                    required
                    value={modalData.morningBusNumber}
                    onChange={(e) => setModalData({...modalData, morningBusNumber: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select morning bus...</option>
                    {buses.map(bus => (
                      <option key={bus.BusID} value={bus.BusNumber}>
                        Bus #{bus.BusNumber} {bus.Notes ? `(${bus.Notes})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center gap-2">
                      <Moon size={16} className="text-indigo-500" />
                      Evening Bus *
                    </div>
                  </label>
                  <select
                    required
                    value={modalData.eveningBusNumber}
                    onChange={(e) => setModalData({...modalData, eveningBusNumber: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select evening bus...</option>
                    {buses.map(bus => (
                      <option key={bus.BusID} value={bus.BusNumber}>
                        Bus #{bus.BusNumber} {bus.Notes ? `(${bus.Notes})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Address Configuration */}
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            {lastAddrHint && (
              <p className="mb-3 text-xs text-blue-600 flex items-start gap-1">
                <History size={12} className="mt-0.5 flex-shrink-0" /> <span>last year's address: <span className="font-semibold">{lastAddrHint}</span>{lastYear?.AddressNotes ? ` (notes: ${lastYear.AddressNotes})` : ''}</span>
              </p>
            )}
            <div className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                id="useCustomAddress"
                checked={modalData.useCustomAddress}
                onChange={(e) => handleUseCustomAddressChange(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="useCustomAddress" className="text-sm font-semibold text-gray-700 cursor-pointer">
                Use custom pickup/dropoff address (different from family address)
              </label>
            </div>

            {modalData.useCustomAddress && (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="differentAddresses"
                    checked={modalData.hasDifferentAddresses}
                    onChange={(e) => handleDifferentAddressesChange(e.target.checked)}
                    disabled={modalData.busService !== 'Both'}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                  />
                  <label htmlFor="differentAddresses" className="text-sm font-semibold text-gray-700 cursor-pointer">
                    Different pickup and dropoff addresses
                    {modalData.busService !== 'Both' && (
                      <span className="text-xs text-gray-500 ml-2">(Only for "Both Ways")</span>
                    )}
                  </label>
                </div>

                {!modalData.hasDifferentAddresses ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-gray-500" />
                        {modalData.busService === 'MorningOnly' ? 'Pickup Address *' :
                         modalData.busService === 'EveningOnly' ? 'Dropoff Address *' :
                         'Pickup/Dropoff Address *'}
                      </div>
                    </label>
                    <textarea
                      required
                      value={modalData.morningAddress}
                      onChange={(e) => handleSameAddressChange(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows="2"
                      placeholder="Enter full address..."
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {modalData.busService !== 'EveningOnly' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center gap-2">
                            <Sun size={16} className="text-orange-500" />
                            Morning Pickup Address *
                          </div>
                        </label>
                        <textarea
                          required
                          value={modalData.morningAddress}
                          onChange={(e) => setModalData({...modalData, morningAddress: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          rows="2"
                          placeholder="Pickup address..."
                        />
                      </div>
                    )}

                    {modalData.busService !== 'MorningOnly' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center gap-2">
                            <Moon size={16} className="text-indigo-500" />
                            Evening Dropoff Address *
                          </div>
                        </label>
                        <textarea
                          required
                          value={modalData.eveningAddress}
                          onChange={(e) => setModalData({...modalData, eveningAddress: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          rows="2"
                          placeholder="Dropoff address..."
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address Notes (Optional)
                  </label>
                  <textarea
                    value={modalData.addressNotes}
                    onChange={(e) => setModalData({...modalData, addressNotes: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="Special instructions for driver (e.g., building number, landmark, gate code...)"
                  />
                </div>
              </>
            )}

            {!modalData.useCustomAddress && familyAddress && (
              <div className="bg-white border border-gray-200 rounded p-3 flex items-start gap-2">
                <Home size={16} className="text-gray-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-600">Using Family Address:</p>
                  <p className="text-sm text-gray-800">{familyAddress}</p>
                </div>
              </div>
            )}
          </div>

          {/* Enrollment Summary */}
          {selectedStudent && selectedLine && (
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Enrollment Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Student:</span>
                  <span className="font-medium text-gray-800">{modalData.studentName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Line:</span>
                  <span className="font-medium text-gray-800">
                    Line {selectedLine.LineNumber} - {selectedLine.LineName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-medium text-gray-800">
                    {modalData.busService === 'Both' ? '🌅🌙 Both Ways' :
                     modalData.busService === 'MorningOnly' ? '🌅 Morning Only' :
                     '🌙 Evening Only'}
                  </span>
                </div>
                {modalData.morningBusNumber && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {modalData.hasDifferentBuses ? '🌅 Morning Bus:' : 'Bus:'}
                    </span>
                    <span className="font-medium text-gray-800">Bus #{modalData.morningBusNumber}</span>
                  </div>
                )}
                {modalData.hasDifferentBuses && modalData.eveningBusNumber && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">🌙 Evening Bus:</span>
                    <span className="font-medium text-gray-800">Bus #{modalData.eveningBusNumber}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-gray-300 pt-2 mt-2">
                  <span className="text-gray-700 font-semibold">Amount:</span>
                  <span className="text-xl font-bold text-blue-600">
                    ${selectedLine.TrimesterPrice?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading || !modalData.studentId || !modalData.lineId || (priorYearInfo?.blockEnrollment && !overrideBusBalance)}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Enrolling...
                </div>
              ) : (
                'Enroll Student'
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              disabled={loading}
              className="px-8 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusEnrollModal;